// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { userSlice } from '@shared/data-access/slices/user';
import {
  AdvisorRole,
  type CitecUser,
} from '@shared/data-access/interfaces/api/engine/citec';
import { useAppDispatch } from '@advisor-app/store/store';
import { useEffect, useState, useCallback } from 'react';
import { userApi as rtkUserApi } from '@advisor-app/store/api/engine/user-api';
import { useSafeUpdate } from '@shared/ui-kit/hooks';

const dispatchUserFromApi = (
  dispatch: ReturnType<typeof useAppDispatch>,
  data: CitecUser,
) => {
  dispatch(
    userSlice.actions.setUserBase({
      first_name: data.first_name,
      second_name: data.second_name,
      first_surname: data.first_surname,
      second_surname: data.second_surname,
      phone_number: data.phone_number,
      user_id: data.user_id,
    }),
  );
  dispatch(userSlice.actions.setOrganizations(data.organizations ?? []));
  dispatch(userSlice.actions.setLoginType(data.login_type ?? {}));
  dispatch(userSlice.actions.setRole(data.role ?? AdvisorRole.AdvisorNotAgent));
};

export const useValidateRegisteredUser = () => {
  const dispatch = useAppDispatch();
  const runSafeUpdate = useSafeUpdate();
  const [isUserInDb, setIsUserInDb] = useState(false);

  const [getUserData] = rtkUserApi.useLazyGetUserQuery();
  const [postUser] = rtkUserApi.usePostUserMutation();

  const validateRegisteredUserInDb = useCallback(async () => {
    /* istanbul ignore next */
    try {
      const data = await getUserData().unwrap();

      runSafeUpdate(() => {
        dispatchUserFromApi(dispatch, data);
      });

      return !!data.user_id;
    } catch (error) {
      console.error('[validateRegisteredUserInDb]', error);
      return false;
    }
  }, [dispatch, getUserData, runSafeUpdate]);

  const registerUserInDb = useCallback(async () => {
    try {
      const data = await postUser().unwrap();

      runSafeUpdate(() => {
        dispatchUserFromApi(dispatch, data);
      });

      /* istanbul ignore next */
      return !!data.user_id;
    } catch (error) {
      console.error('[registerUserInDb]', error);
      return false;
    }
  }, [dispatch, postUser, runSafeUpdate]);

  const runValidateRegisteredUserInDb = async () => {
    try {
      const isRegisteredUser = await validateRegisteredUserInDb();

      runSafeUpdate(() => setIsUserInDb(isRegisteredUser));
      /* istanbul ignore else */
      if (!isRegisteredUser) registerUserInDb();
    } catch (error) {
      /* istanbul ignore next */
      console.error('[runValidateRegisteredUserInDb]', error);
    }
  };

  useEffect(() => {
    runValidateRegisteredUserInDb();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isUserInDb,
    registerUserInDb,
  };
};
